/**
 * Month-over-month background tint.
 *
 * +100% vs prior month → strongest green
 * −90% vs prior month → strongest red
 * Nearer to 0% → proportionally lighter tint
 */

/** Max green intensity at this gain ratio (+100%). */
export const MOM_GREEN_FULL_AT = 1

/** Max red intensity at this drop ratio (−90%). */
export const MOM_RED_FULL_AT = 0.9

/** Peak colour-mix % into the cell background (keeps text readable). */
export const MOM_TINT_MAX_MIX_PCT = 46

/**
 * Signed change ratio vs previous month.
 * Returns ``null`` when there is no prior month (e.g. January).
 * Zero→positive is treated as +100%; zero→zero as 0.
 */
export function monthOverMonthChangeRatio(
  current: number,
  previous: number | null | undefined,
): number | null {
  if (previous == null || !Number.isFinite(previous)) return null
  if (!Number.isFinite(current)) return 0
  if (previous === 0 && current === 0) return 0
  if (previous === 0) return MOM_GREEN_FULL_AT
  return (current - previous) / previous
}

/** 0..1 intensity for green (gains) or red (drops). */
export function monthOverMonthTintStrength(changeRatio: number | null): {
  tone: 'none' | 'up' | 'down'
  strength: number
} {
  if (changeRatio == null || changeRatio === 0 || !Number.isFinite(changeRatio)) {
    return { tone: 'none', strength: 0 }
  }
  if (changeRatio > 0) {
    return {
      tone: 'up',
      strength: Math.min(changeRatio / MOM_GREEN_FULL_AT, 1),
    }
  }
  return {
    tone: 'down',
    strength: Math.min(-changeRatio / MOM_RED_FULL_AT, 1),
  }
}

/** Inline background style for a month cell, or undefined when untinted. */
export function monthOverMonthTintStyle(
  changeRatio: number | null,
): { backgroundColor: string } | undefined {
  const { tone, strength } = monthOverMonthTintStrength(changeRatio)
  if (tone === 'none' || strength <= 0) return undefined
  const mixPct = Math.round(strength * MOM_TINT_MAX_MIX_PCT)
  const colour = tone === 'up' ? 'var(--colour-success)' : 'var(--colour-danger)'
  return {
    backgroundColor: `color-mix(in srgb, ${colour} ${mixPct}%, var(--colour-bg))`,
  }
}

export function formatMomChangeLabel(changeRatio: number | null): string | null {
  if (changeRatio == null || !Number.isFinite(changeRatio)) return null
  if (changeRatio === 0) return 'unchanged vs prior month'
  const pct = Math.round(changeRatio * 1000) / 10
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct}% vs prior month`
}
